import type { CSSProperties } from 'react';
import { cn } from '../../lib/cn.ts';

export interface LevelMeterProps {
  /** How loud it is right now, `0`–`1`. Anything past `1` reads as full. */
  value: number;
  /** What is being measured, for anyone who cannot see the bar move. */
  label: string;
  className?: string;
}

/**
 * A bar that moves with a live signal.
 *
 * Its whole job is answering "is this microphone the one hearing me", which no
 * amount of device names can answer: the labels the browser hands out are the
 * driver's, and on a laptop with a dock and a headset three of them are
 * plausible. Speaking and watching the bar is the only test.
 *
 * It reports itself as a meter rather than a progress bar: nothing here is on
 * its way to being finished, it is a reading that goes up and down.
 */
export function LevelMeter({ value, label, className }: LevelMeterProps) {
  const filled = Math.min(Math.max(value, 0), 1);

  return (
    <div
      role='meter'
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={1}
      aria-valuenow={Number(filled.toFixed(2))}
      className={cn('h-2 w-full overflow-hidden rounded-full bg-fill', className)}
    >
      {/*
       * Width rather than a transform: the bar is a length being read off, and
       * a scaled one would stretch its own rounded end into an oval.
       */}
      <div
        data-slot='level-meter-fill'
        className='h-full rounded-full bg-accent transition-[width] duration-75 ease-out'
        style={{ width: `${filled * 100}%` } satisfies CSSProperties}
      />
    </div>
  );
}
